import { ArrowDownWideNarrow, Check } from 'lucide-react'
import { useRef, useState, type KeyboardEvent } from 'react'
import type { FeedSort } from '../../api/types'
import { Dialog } from '../Dialog'
import { Select } from '../Select'

interface FeedSortControlProps {
  desktop: boolean
  value: FeedSort
  options: { value: FeedSort; label: string }[]
  onChange: (value: FeedSort) => void
}

function keepFocusInSheet(event: KeyboardEvent) {
  if (event.key !== 'Tab' || !(event.target instanceof HTMLElement)) return
  const controls = event.target.closest('[role="dialog"]')?.querySelectorAll<HTMLButtonElement>('button')
  if (!controls?.length) return
  const first = controls[0]
  const last = controls[controls.length - 1]
  if (event.target !== (event.shiftKey ? first : last)) return
  event.preventDefault()
  const next = event.shiftKey ? last : first
  next?.focus()
}

export function FeedSortControl({ desktop, value, options, onChange }: FeedSortControlProps) {
  const [open, setOpen] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)

  if (desktop) {
    return (
      <Select className="feed-sort" label="Сортировка" value={value} onChange={(event) => onChange(event.target.value as FeedSort)}>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </Select>
    )
  }

  function close() {
    setOpen(false)
    trigger.current?.focus()
  }

  return (
    <div className="ml-auto flex min-w-0 justify-end" onKeyDown={keepFocusInSheet}>
      <button
        type="button"
        ref={trigger}
        aria-label="Сортировка"
        aria-haspopup="dialog"
        aria-expanded={open}
        className="feed-sort inline-flex min-h-11 max-w-full items-center gap-1.5 rounded-lg px-2 text-xs font-semibold text-link hover:bg-surface"
        onClick={() => setOpen(true)}
      >
        <ArrowDownWideNarrow size={16} className="shrink-0" aria-hidden />
        <span className="truncate">{options.find((option) => option.value === value)?.label ?? 'Сортировка'}</span>
      </button>
      {open && (
        <Dialog title="Сортировка" onClose={close}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              autoFocus={option.value === value}
              aria-pressed={option.value === value}
              className="flex min-h-12 w-full items-center justify-between gap-2 rounded-lg px-3 text-left text-sm hover:bg-surface aria-pressed:bg-surface aria-pressed:font-semibold aria-pressed:text-link"
              onClick={() => {
                onChange(option.value)
                close()
              }}
            >
              {option.label}
              {option.value === value && <Check size={17} className="shrink-0" aria-hidden />}
            </button>
          ))}
        </Dialog>
      )}
    </div>
  )
}
